/**
 * Pre-quote review values. Built from the same metric/pricing helpers the product
 * deal cards render, so the confirmation popup cannot drift from the card numbers.
 */
import {
    formatPpaRate,
    resolveLivePartnerQuote,
    type PreContractPpwMetricsOverride,
} from './preContractPpwPricing';
import { resolvePreContractSolarMetrics } from './preContractSolarDesignMetrics';
import { buildPreContractRoofDesignSnapshot } from './preContractRoofDesign';
import { countIncludedModules } from './preContractModuleLayout';
import type { PreContractProductPricingOption } from './preContractProductPartners';
import type { PreContractIntakeForm, PreContractProduct } from './preContractTypes';
import type { RetrieveSystemDesignResponse } from '../../services/oneButtonServices/designServices';
import type { PriceQuoteOverrides } from '../../store/oneButtonStore';

export type PreContractQuoteReviewItem = {
    label: string;
    value: string;
};

export type PreContractQuoteReview = {
    product: PreContractProduct;
    partner: string;
    financeMethod: string;
    utilityCompany: string;
    utilityRate: string;
    systemItems: PreContractQuoteReviewItem[];
    financeItems: PreContractQuoteReviewItem[];
};

export type BuildPreContractQuoteReviewArgs = {
    product: PreContractProduct;
    partnerName: string;
    financeMethod: string;
    form: PreContractIntakeForm;
    systemDesign: RetrieveSystemDesignResponse | null;
    priceQuoteOverrides?: PriceQuoteOverrides | null;
    metricsOverride?: PreContractPpwMetricsOverride | null;
    selectedOption?: PreContractProductPricingOption | null;
    summaryItems?: Array<{ label: string; value: string }>;
    preferEnfin?: boolean;
};

const DEFAULT_ESCALATOR = 0.0299;

function formatRatePerKwh(raw: string | number | null | undefined): string {
    const n = typeof raw === 'number' ? raw : Number(String(raw ?? '').replace(/[$,\s]/g, ''));
    return Number.isFinite(n) && n > 0 ? formatPpaRate(n) : '—';
}

function formatPpw(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return `$${value.toFixed(2)}/W`;
}

function formatMoney(value: number | null | undefined, digits = 0): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return `$${value.toLocaleString(undefined, {
        maximumFractionDigits: digits,
        minimumFractionDigits: digits,
    })}`;
}

export function buildPreContractQuoteReview({
    product,
    partnerName,
    financeMethod,
    form,
    systemDesign,
    priceQuoteOverrides = null,
    metricsOverride = null,
    selectedOption = null,
    summaryItems = [],
    preferEnfin = false,
}: BuildPreContractQuoteReviewArgs): PreContractQuoteReview {
    const summaryValue = (label: string) =>
        summaryItems.find((item) => item.label === label)?.value ?? '—';

    const systemItems: PreContractQuoteReviewItem[] = [];
    const financeItems: PreContractQuoteReviewItem[] = [];

    if (product === 'Solar') {
        const metrics = resolvePreContractSolarMetrics({ form, systemDesign, metricsOverride });
        const liveQuote = resolveLivePartnerQuote(
            form,
            selectedOption?.partner ?? partnerName,
            {
                escalator: selectedOption?.escalator ?? DEFAULT_ESCALATOR,
                metricsOverride: metrics,
                preferEnfin,
            },
        );
        const { count } = countIncludedModules(form.roofModuleLayouts, form.roofSelections);

        systemItems.push(
            {
                label: 'System size',
                value: metrics.systemKw ? `${metrics.systemKw.toFixed(2)} kW` : '—',
            },
            {
                label: 'Production',
                value: metrics.annualProductionKwh
                    ? `${Math.round(metrics.annualProductionKwh).toLocaleString()} kWh`
                    : summaryValue('System Production'),
            },
            {
                label: 'Multiplier',
                value: metrics.multiplier ? String(Number(metrics.multiplier.toFixed(3))) : '—',
            },
            { label: 'Modules', value: count > 0 ? String(count) : '—' },
            { label: 'Module type', value: form.moduleTypeName || '—' },
            { label: 'Annual usage', value: summaryValue('Utility Usage') },
        );

        financeItems.push(
            { label: 'Target/Min PPA rate', value: formatRatePerKwh(form.targetMinPpaRate) },
            { label: 'PPW', value: formatPpw(liveQuote?.ppw ?? selectedOption?.ppw ?? null) },
            { label: 'EPC', value: formatMoney(liveQuote?.epc ?? selectedOption?.epc ?? null) },
        );

        const monthly = liveQuote?.estimatedMonthly ?? selectedOption?.monthly ?? null;
        if (monthly != null) {
            financeItems.push({ label: 'Monthly payment', value: `${formatMoney(monthly)}/mo` });
        }
        const escalator = selectedOption?.escalator ?? null;
        if (escalator != null) {
            financeItems.push({
                label: 'Escalator',
                value: `${Math.round(escalator * 10000) / 100}%`,
            });
        }
    } else if (product === 'Battery') {
        systemItems.push(
            {
                label: 'Battery',
                value: [form.batteryManufacturer, form.batteryName].filter(Boolean).join(' ') || '—',
            },
            { label: 'Quantity', value: form.batteryQuantity ? String(form.batteryQuantity) : '—' },
        );
    } else if (product === 'Roof' || product === 'RRR' || product === 'RR') {
        const roof = buildPreContractRoofDesignSnapshot({
            form,
            systemDesign,
            priceQuoteOverrides,
        });
        systemItems.push(
            { label: 'Roof squares', value: roof.total > 0 ? `${roof.total} sq` : '—' },
            { label: 'Shingle color', value: roof.shingleColor || '—' },
        );
    }

    if (selectedOption?.term != null) {
        financeItems.push({ label: 'Term', value: `${selectedOption.term} months` });
    }
    if (selectedOption?.apr != null) {
        financeItems.push({ label: 'APR', value: `${selectedOption.apr}%` });
    }
    if (product !== 'Solar' && selectedOption?.monthly != null) {
        financeItems.push({
            label: 'Monthly payment',
            value: `${formatMoney(selectedOption.monthly)}/mo`,
        });
    }

    return {
        product,
        partner: selectedOption?.partner?.trim() || partnerName,
        financeMethod: selectedOption?.financeMethod?.trim() || financeMethod || '—',
        utilityCompany: form.utilityCompany || '—',
        utilityRate: formatRatePerKwh(form.utilityRate),
        systemItems,
        financeItems,
    };
}
